/* eslint-disable react/prop-types */
import React from "react";
import _ from "lodash";
import { Button, Form, Icon } from "semantic-ui-react";
import { utils } from "@rjsf/core";
import PropTypes from "prop-types";

const { asNumber, guessType } = utils;

const nums = new Set(["number", "integer"]);

function selectValue(value, selected, all) {
  const at = all.indexOf(value);
  const updated = selected.slice(0, at).concat(value, selected.slice(at));
  // As inserting values at predefined index positions doesn't work with empty
  // arrays, we need to reorder the updated selection to match the initial order
  return updated.sort((a, b) => all.indexOf(a) > all.indexOf(b));
}

function deselectValue(value, selected) {
  return selected.filter((v) => v !== value);
}

/**
 * * Returns and creates an array format required for semantic drop down
 * @param {array} enumOptions- array of items for the dropdown
 * @param {array} enumDisabled - array of enum option values to disable
 * @returns {*}
 */
function createDefaultValueOptionsForDropDown(
  enumOptions,
  enumDisabled,
  enumIcons = [],
  enumColors = []
) {
  const disabledOptions = enumDisabled || [];
  let options = [];
  // eslint-disable-next-line no-shadow
  options = _.map(enumOptions, ({ label, value }, index) => ({
    disabled: disabledOptions.indexOf(value) !== -1,
    key: label,
    text: label,
    icon: enumIcons[index],
    color: enumColors[index],
    value,
  }));
  return options;
}

/**
 * This is a silly limitation in the DOM where option change event values are
 * always retrieved as strings.
 */
const processValue = (schema, value) => {
  // "enum" is a reserved word, so only "type" and "items" can be destructured
  const { type, items } = schema;
  if (value === "") {
    return undefined;
  } else if (type === "array" && items && nums.has(items.type)) {
    return Array.isArray(value) ? value.map(asNumber) : [];
  } else if (type === "boolean") {
    return value === "true" || value === true;
  } else if (type === "number") {
    return asNumber(value);
  }

  // If type is undefined, but an enum is present, try and infer the type from
  // the enum values
  if (schema.enum) {
    if (schema.enum.every((x) => guessType(x) === "number")) {
      return asNumber(value);
    } else if (schema.enum.every((x) => guessType(x) === "boolean")) {
      return value === "true";
    }
  }

  return value;
};

function ButtonGroupWidget({
  schema,
  uiSchema,
  id,
  options,
  name,
  required,
  disabled,
  readonly,
  value,
  multiple,
  placeholder,
  autofocus,
  onChange,
  onBlur,
  onFocus,
  ...props
}) {
  const uiOptions = utils.getUiOptions(uiSchema || {});
  const semanticProps = options.semantic;

  const { enumDisabled, enumOptions, enumColors, enumIcons } = options;
  const emptyValue = multiple ? [] : "";
  const dropdownOptions = createDefaultValueOptionsForDropDown(
    enumOptions,
    enumDisabled,
    enumIcons,
    enumColors
  );
  const _onChange = (event, { value: btnValue, ...props }) => {
    if (schema.type === "array") {
      const all = enumOptions.map((option) => option.value);
      value = Array.isArray(value) ? value : emptyValue;
      const checked = value.includes(btnValue);
      const newValue = checked
        ? deselectValue(btnValue, value)
        : selectValue(btnValue, value, all);
      onChange && onChange(newValue);
    } else {
      onChange && onChange(processValue(schema, btnValue));
    }
  };
  // eslint-disable-next-line no-shadow
  const _onBlur = ({ target: { value } }) =>
    onBlur && onBlur(id, processValue(schema, value));
  const _onFocus = ({
    // eslint-disable-next-line no-shadow
    target: { value },
  }) => onFocus && onFocus(id, processValue(schema, value));

  /*
      <Form.Select
      key={id}
      name={name}
      multiple={typeof multiple === "undefined" ? false : multiple}
      value={typeof value === "undefined" ? emptyValue : value}
      disabled={disabled}
      {...semanticProps}
      label={schema.title || label}
      clearable={!required}
      placeholder={placeholder || options.placeholder}
      selection
      required={required}
      autoFocus={autofocus}
      readOnly={readonly}
      options={dropdownOptions}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
    />
   */

  const label = props.label || schema.title;

  return (
    <Form.Field key={id} required={required} disabled={readonly}>
      {dropdownOptions.length ? (
        <Button.Group size={semanticProps.size} positive>
          {dropdownOptions.map((option) => {
            const checked = Array.isArray(value)
              ? value.includes(option.value)
              : value === option.value;
            const itemDisabled =
              enumDisabled && enumDisabled.indexOf(option.value) !== -1;
            return (
              <Button
                type="button"
                primary={
                  checked &&
                  !option.color &&
                  !semanticProps.positive &&
                  !semanticProps.negative
                }
                color={
                  checked && !semanticProps.positive && !semanticProps.negative
                    ? option.color
                    : undefined
                }
                positive={checked && semanticProps.positive}
                negative={checked && semanticProps.negative}
                basic={!checked}
                active={checked}
                value={option.value}
                content={option.text}
                icon={
                  option.icon ? (
                    <Icon
                      name={option.icon}
                      color={!checked ? option.color : undefined}
                    />
                  ) : null
                }
                onClick={_onChange}
                disabled={itemDisabled}
              />
            );
          })}
        </Button.Group>
      ) : (
        <p> No options </p>
      )}
    </Form.Field>
  );
}

ButtonGroupWidget.defaultProps = {
  options: {
    semantic: {
      inverted: "false",
      fluid: true,
      selection: true,
      scrolling: true,
      upward: false,
    },
  },
};

ButtonGroupWidget.propTypes = {
  options: PropTypes.object,
};

export default ButtonGroupWidget;
